/**
 * @file participantSearch.ts
 * @brief Shared by BoardroomSetupPage and ExternalFromSearch (kept out of the
 *        component file so fast refresh keeps working).
 *
 * @author DES
 * @date 30-09-2026
 */

import { useEffect, useState } from "react";
import { apiGet } from "@/shared/api/client";
import { meetingsEndpoints } from "@/shared/api/endpoints";

export type GuestLookup =
  | { status: "idle" }
  | { status: "checking" }
  | {
      status: "found";
      name: string;
      hasEnrolledVoice?: boolean;
      nativeLanguage?: string | null;
      profileImageUrl?: string | null;
    }
  | { status: "notfound" }
  | { status: "inlist"; name?: string; reason?: string };

import { FULL_EMAIL_RE } from "./newPerson";
export { FULL_EMAIL_RE };

/** Whether the block has anything to show for this search text. */
export function hasExternalBlock(query: string): boolean {
  const text = query.trim();
  if (!text) return false;
  return FULL_EMAIL_RE.test(text) || !text.includes("@");
}

/**
 * The email lookup behind the search (`/api/meetings/participant-lookup`),
 * debounced 450 ms, for a search text that is a full email; `idle`
 * otherwise. Shared by Boardroom setup's participant picker and the meeting
 * page's speaker picker (02-10-2026).
 */
export function useGuestLookup(query: string): GuestLookup {
  const [lookup, setLookup] = useState<GuestLookup>({ status: "idle" });
  useEffect(() => {
    const email = query.trim();
    if (!FULL_EMAIL_RE.test(email)) {
      setLookup({ status: "idle" });
      return;
    }
    let cancelled = false;
    setLookup({ status: "checking" });
    const t = window.setTimeout(async () => {
      try {
        const res = await apiGet<{
          isRegistered?: boolean;
          inYourList?: boolean;
          reason?: string;
          name?: string;
          hasEnrolledVoice?: boolean;
          nativeLanguage?: string | null;
          profileImageUrl?: string | null;
        }>(`${meetingsEndpoints.participantLookup}?email=${encodeURIComponent(email)}`);
        if (cancelled) return;
        if (res.isRegistered && res.inYourList) {
          setLookup({ status: "inlist", name: res.name, reason: res.reason });
        } else if (res.isRegistered) {
          setLookup({
            status: "found",
            name: res.name ?? email,
            hasEnrolledVoice: res.hasEnrolledVoice,
            nativeLanguage: res.nativeLanguage,
            profileImageUrl: res.profileImageUrl,
          });
        } else {
          setLookup({ status: "notfound" });
        }
      } catch {
        if (!cancelled) setLookup({ status: "idle" });
      }
    }, 450);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [query]);
  return lookup;
}
